// src/tour/scripts/welcomeScript.js
//
// The first-run "Welcome to Casavio" tour. Guides a new user from an empty
// account to a printed Emergency Sheet — the artifact-as-payoff.
//
// Anchors reference real DOM hooks via [data-tour-anchor="..."] attributes the
// host surface (Dashboard) renders. See src/tour/components/TourStep.js and the
// anchor resolver in TourOverlay.js.
//
// Step types:
//   choice_card  — opening fork (show me / explore / help-aware)
//   instruction  — anchored bubble with a CTA that triggers a host action
//   in_flow      — bubble shown while the user works inside another flow
//   pointer_tour — sequential pointers at several anchors, then finish
//
// Copy can be refined later. Structure first. (Tone Principle #1: never comment
// on the state of the user's home; no exclamation points outside genuine wow
// moments.)

import { TOUR_ACTIONS, TOUR_EVENT_TYPES } from '../../lib/appEvents'

export const welcomeScript = {
  id: 'welcome',
  name: 'Welcome to Casavio',
  estimatedMinutes: 5,
  endsWith: 'A printed Emergency Sheet for your fridge',
  steps: [
    {
      id: 'choice',
      type: 'choice_card',
      countsTowardProgress: true,
      title: 'Welcome to your new home in Casavio.',
      body: 'Casavio keeps the details of your home in one place — appliances, contacts, and the things you reach for in an emergency.',
      options: [
        {
          label: 'Show me around — 5 minutes, ends with a printed Emergency Sheet for your fridge',
          action: 'continue',
        },
        { label: "I'll explore on my own", action: 'skip_tour', dismissReason: 'self_explore' },
        { label: 'Just getting started? Open Help anytime', action: 'skip_tour', dismissReason: 'help_aware' },
      ],
    },
    {
      id: 'add_appliance_intro',
      type: 'instruction',
      countsTowardProgress: true,
      anchor: 'dashboard.home_profile_tab',
      title: "Let's add your first appliance",
      body: "Start with your refrigerator, washer, or dryer — we're especially good at finding manuals for these. Appliances live in your Home Profile.",
      footerLine: "You'll end with a printed Emergency Sheet for your fridge.",
      cta: { label: 'Add an appliance', action: TOUR_ACTIONS.OPEN_APPLIANCE_ADD },
    },
    {
      id: 'appliance_photo',
      type: 'in_flow',
      countsTowardProgress: true,
      title: 'Take a photo of the data plate',
      body: 'That’s the sticker with the model number — usually on the back, the side, or just inside the door.',
      footerLine: "You'll end with a printed Emergency Sheet for your fridge.",
      remindLaterAllowed: true, // "Don't have access? Remind me later"
      // Wait for the AddAppliance flow to resolve, not a tour "Continue":
      waitsForSignal: [
        TOUR_EVENT_TYPES.APPLIANCE_SAVED,
        TOUR_EVENT_TYPES.APPLIANCE_FLOW_CLOSED,
      ],
      advanceOn: { event: TOUR_EVENT_TYPES.APPLIANCE_SAVED, to: 'next' },                      // saved → wow_moment
      cancelOn: { event: TOUR_EVENT_TYPES.APPLIANCE_FLOW_CLOSED, to: 'add_emergency_contact' }, // closed w/o save → skip wow
      closeOnLeave: { action: TOUR_ACTIONS.CLOSE_APPLIANCE_ADD },
    },
    {
      id: 'wow_moment',
      type: 'in_flow',
      countsTowardProgress: true,
      title: 'Hang on, we’re looking up the manual…',
      body: 'Casavio is searching the web for your appliance’s manual right now.',
      watchesEvent: 'manual_lookup_resolved',
      onHit: {
        title: 'Found it.',
        body: 'Here’s the manual — it’s now linked to your appliance, ready whenever you need it.',
        cta: { label: 'Continue', action: 'continue' },
      },
      onMiss: {
        title: "Couldn't find it automatically",
        body: 'No problem — you can add the manual later, or snap a clearer photo of the data plate.',
        cta: { label: 'Continue', action: 'continue' },
      },
      onTimeout: {
        title: 'Still looking…',
        body: 'This one’s taking a moment. You can keep going and we’ll keep searching in the background.',
        cta: { label: 'Continue', action: 'continue' },
      },
    },
    {
      id: 'add_emergency_contact',
      type: 'in_flow',
      countsTowardProgress: true,
      anchor: 'dashboard.emergency',
      title: 'Add one emergency contact',
      body: 'A neighbor, family member, or trusted friend. We’ll put them on the printable sheet.',
      cta: { label: 'Add a contact', action: TOUR_ACTIONS.FOCUS_EMERGENCY },
      waitsForSignal: [
        TOUR_EVENT_TYPES.EMERGENCY_CONTACT_SAVED,
        TOUR_EVENT_TYPES.EMERGENCY_CONTACT_FLOW_CLOSED,
      ],
      advanceOn: { event: TOUR_EVENT_TYPES.EMERGENCY_CONTACT_SAVED, to: 'next' },                // saved → generate_sheet
      cancelOn: { event: TOUR_EVENT_TYPES.EMERGENCY_CONTACT_FLOW_CLOSED, to: 'closing_pointer' }, // closed w/o save → skip sheet
      closeOnLeave: { action: TOUR_ACTIONS.CLOSE_EMERGENCY_CONTACTS },
    },
    {
      id: 'generate_sheet',
      type: 'in_flow',
      countsTowardProgress: true,
      title: 'Your Emergency Sheet is ready',
      body: 'Print it and put it on the fridge. EMTs, neighbors, and family can use it in a pinch.',
      cta: { label: 'Print / Save / Email', action: TOUR_ACTIONS.OPEN_SHEET_ACTIONS },
      // Advance only when the wizard actually CLOSES (after the user prints / clicks
      // Done on the celebration screen) — NOT on 'generated'. The PDF is delivered
      // from the celebration's "Open & Print" button, which renders only after
      // generation; closing on 'generated' would unmount the wizard before the user
      // can print. ('emergency_sheet_generated' is still emitted, just not a trigger.)
      waitsForSignal: [
        TOUR_EVENT_TYPES.EMERGENCY_SHEET_FLOW_CLOSED,
      ],
      advanceOn: { event: TOUR_EVENT_TYPES.EMERGENCY_SHEET_FLOW_CLOSED, to: 'next' }, // wizard closed → closing_pointer
      closeOnLeave: { action: TOUR_ACTIONS.CLOSE_SHEET_WIZARD }, // closes the wizard if the user hits "Skip step"
    },
    {
      id: 'closing_pointer',
      type: 'pointer_tour',
      countsTowardProgress: true,
      title: 'Here’s where everything else lives',
      body: 'A quick pointer tour of your dashboard.',
      pointers: [
        {
          anchor: 'dashboard.home_profile_tab',
          label: 'Home Profile',
          body: 'Appliances, rooms, and systems all live here.',
        },
        {
          anchor: 'dashboard.maintenance_tab',
          label: 'Maintenance & work orders',
          body: 'Track upkeep and the jobs you line up.',
        },
        {
          anchor: 'dashboard.services_tab',
          label: 'Services',
          body: 'Recurring services and the contractors you trust.',
        },
        {
          anchor: 'avatar.help_menu',
          label: 'Help & Tours — always here',
          body: 'Open this menu any time to replay a tour.',
        },
      ],
    },
  ],
}
